import {
  LTLShiplifyLookupMetadata,
  LTLShiplifyLookupStatus,
} from 'lib/api/getLTLQuickQuotes';
import { getShiplifyLocationLookup } from 'lib/api/getShiplifyLocationLookup';
import {
  LTLAccessorial,
  LTLSelectedAccessorial,
  QuickQuoteInputs,
} from 'pages/QuoteView/Quoting/RequestQuickQuote/types';
import { StopTypes } from 'types/Appointment';

import { sortSelectedAccessorials } from './accessorialUtils';
import { SHIPLIFY_STOP_TYPES } from './shiplifyConsts';
import {
  getShiplifyStop,
  isShiplifyDestinationComplete,
  matchShiplifyRecommendations,
} from './shiplifyLookup';

function failedLookup(): LTLShiplifyLookupMetadata {
  return { status: LTLShiplifyLookupStatus.FailedOpen };
}

export async function applyMissingShiplifyLookups(
  formValues: QuickQuoteInputs,
  accessorials: LTLAccessorial[],
  options?: {
    requireLocationLineFullAddress?: boolean;
    skipAddressBookStops?: boolean;
  }
): Promise<QuickQuoteInputs> {
  const existingLookups = formValues.ltl?.shiplifyLookups ?? {};
  const results = await Promise.all(
    SHIPLIFY_STOP_TYPES.map(async (stopType, index) => {
      const destination = getShiplifyStop(formValues.stops, index, options);

      if (!destination || !isShiplifyDestinationComplete(destination)) {
        return [stopType, undefined, false] as const;
      }

      if (existingLookups[stopType]) {
        return [stopType, existingLookups[stopType], false] as const;
      }

      // Only the automatic lookup is skipped: a lookup the rep already ran (and its
      // accessorials) stays, as an empty result here would invalidate the stop.
      if (
        options?.skipAddressBookStops &&
        Boolean(destination.externalTMSID?.trim())
      ) {
        return [stopType, undefined, false] as const;
      }

      const result = await getShiplifyLocationLookup(stopType, {
        ...destination,
        addressLine1: destination.addressLine1!,
        country: destination.country!,
      });

      return [
        stopType,
        result.isOk() ? result.value : failedLookup(),
        true,
      ] as const;
    })
  );

  const lookups = { ...existingLookups };
  const invalidatedStops = new Set<StopTypes>();
  results.forEach(([stopType, lookup]) => {
    if (lookup) {
      lookups[stopType] = lookup;
    } else {
      delete lookups[stopType];
      invalidatedStops.add(stopType);
    }
  });

  const selections: LTLSelectedAccessorial[] = (
    formValues.ltl?.selectedAccessorials ?? []
  ).filter(
    (selection) =>
      !selection.recommendedByShiplifyStop ||
      !invalidatedStops.has(selection.recommendedByShiplifyStop as StopTypes)
  );
  const selectedIds = new Set(
    selections.map(
      (selection) => selection.code || String(selection.externalId ?? '')
    )
  );

  results.forEach(([stopType, lookup, wasFetched]) => {
    // Existing recommendations may have been dismissed. Only a fresh lookup
    // can introduce another automatic selection during submission.
    if (!wasFetched) {
      return;
    }

    const matches = matchShiplifyRecommendations(
      lookup?.recommendations ?? [],
      accessorials,
      stopType
    );

    matches.autoApply.forEach(({ accessorial, recommendation, code }) => {
      if (!accessorial) {
        return;
      }

      const identifier =
        accessorial.code || String(accessorial.externalId ?? '');

      if (!identifier || selectedIds.has(identifier)) {
        return;
      }

      selectedIds.add(identifier);
      selections.push({
        code: accessorial.code,
        name: accessorial.name,
        externalId: accessorial.externalId,
        recommendedByShiplify: true,
        recommendedByShiplifyStop: stopType,
        shiplifyRecommendationCode: code,
        shiplifyConfidence: recommendation.confidence,
        shiplifyAccuracy: recommendation.accuracy,
        actionValues: accessorial.actions
          ? Object.fromEntries(
              Object.keys(accessorial.actions).map((key) => [key, ''])
            )
          : undefined,
      });
    });
  });

  return {
    ...formValues,
    ltl: {
      ...formValues.ltl!,
      selectedAccessorials: sortSelectedAccessorials(selections, accessorials),
      shiplifyLookups: lookups,
    },
  };
}
